import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import InfoTip from "@/components/InfoTip";
import PremiumGate from "@/components/PremiumGate";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { ArrowRight, TrendingUp, Activity, Loader2 } from "lucide-react";

const SOPH_LABEL = ["—", "Básico", "Intermedio", "Avanzado", "Experto"];

function SophScoreBar({ score }) {
  if (score == null) {
    return <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[#A3A3A3]">—</span>;
  }
  const filled = Math.round(score);
  return (
    <div className="inline-flex items-center gap-2">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`w-3 h-3 ${i <= filled ? "bg-[#D90429]" : "bg-[#E5E5E5]"}`}
          />
        ))}
      </div>
      <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[#404040]">
        {SOPH_LABEL[filled]} · {score.toFixed(2)}
      </span>
    </div>
  );
}

export default function Trends() {
  const { t } = useLang();
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minCount, setMinCount] = useState(1);
  const [gated, setGated] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/trends/actors", { params: { min_count: minCount } });
      setActors(r.data);
      setGated(false);
    } catch (e) {
      if (e?.response?.status === 403 && e?.response?.data?.detail?.code === "premium_required") {
        setGated(true);
      } else {
        console.warn("trends load failed", e);
      }
    } finally {
      setLoading(false);
    }
  }, [minCount]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="trends-page">
      <Header />
      {gated ? (
        <PremiumGate testId="trends-premium-gate" />
      ) : (
      <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-10 md:py-16 space-y-10">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#D90429] mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-[#D90429]" />
              · TENDENCIAS · MONITORIZACIÓN
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl font-bold tracking-tighter leading-none mb-4">
              {t("trends_title")}
            </h1>
            <p className="text-[#404040] text-lg max-w-2xl">{t("trends_subtitle")}</p>
          </div>
          <div className="md:col-span-4 border border-[#171717] bg-white p-4">
            <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">
              {t("trends_min_filter")}
            </label>
            <div className="flex gap-0 border border-[#0A0A0A]" data-testid="min-count-filter">
              {[1, 2, 3, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`min-${n}`}
                  onClick={() => setMinCount(n)}
                  className={`flex-1 py-2 font-mono-tech text-xs uppercase tracking-widest border-r border-[#0A0A0A] last:border-r-0 ${
                    minCount === n ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F5F5F5]"
                  }`}
                >
                  ≥{n}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading && (
          <div className="font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> ···
          </div>
        )}

        {!loading && actors.length === 0 && (
          <div className="border border-[#171717] bg-white p-10 text-center">
            <Activity className="w-8 h-8 mx-auto mb-4 text-[#A3A3A3]" />
            <p className="font-serif-display text-xl max-w-xl mx-auto">{t("trends_no_data")}</p>
            <Link
              to="/app"
              className="mt-6 inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest border border-[#0A0A0A] px-4 py-2 hover:bg-[#0A0A0A] hover:text-white"
            >
              → Panel
            </Link>
          </div>
        )}

        {actors.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#171717] border border-[#171717]" data-testid="actors-grid">
            {actors.map((a) => (
              <Link
                key={a.tag}
                to={`/app/trends/${encodeURIComponent(a.tag)}`}
                data-testid={`actor-${a.tag}`}
                className="bg-white p-6 group hover:bg-[#FAFAFA] transition-colors flex flex-col"
              >
                <div className="flex items-baseline justify-between mb-4">
                  <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429]">
                    · ACTOR
                  </span>
                  <span className="font-mono-tech text-[10px] tracking-widest text-[#A3A3A3]">
                    {new Date(a.last_seen).toLocaleDateString()}
                  </span>
                </div>
                <h2 className="font-serif-display text-3xl font-bold leading-none mb-4 break-all">
                  #{a.tag}
                </h2>
                <div className="flex items-baseline gap-2 mb-4">
                  <TrendingUp className="w-4 h-4 text-[#D90429]" />
                  <span className="font-serif-display text-4xl font-bold leading-none">{a.count}</span>
                  <span className="font-mono-tech text-[10px] tracking-widest text-[#A3A3A3] uppercase">
                    {t("trends_count")}
                  </span>
                </div>
                <div className="mt-auto space-y-2 border-t border-[#E5E5E5] pt-3">
                  <div className="flex justify-between items-center font-mono-tech text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                    <span><InfoTip label={t("trends_avg_soph")} tip={t("tip_sophistication")} testid="tip-soph-trends" /></span>
                    <SophScoreBar score={a.avg_sophistication_score} />
                  </div>
                  <div className="flex justify-between font-mono-tech text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                    <span>{t("trends_first_seen")}</span>
                    <span>{new Date(a.first_seen).toLocaleDateString()}</span>
                  </div>
                  <div className="pt-3 inline-flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A] group-hover:text-[#D90429]">
                    {t("trends_view_detail")} <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
